import type {
  LanguageCodeEnum,
  ProductsBySkusQuery,
  ProductVariantWatchBasicDetailsQuery,
} from '@/graphql/saleor/api';
import {
  DETAIL_PAGE_IMAGE_SIZE,
  DETAIL_PAGE_LARGE_IMAGE_SIZE,
  NEXT_PUBLIC_IMAGES_FORMAT,
  STRAP_SIZES_FETCH_LIMIT,
} from '@/lib/const';
import { paths } from '@/lib/paths';

import { createDebouncedFunction } from '../service/api';

export const fetchStrapSizes = async (
  channel: string,
  languageCode: string,
  skus: string[],
) => {
  const url = paths.api.product.strapSizes.asPath({
    query: {
      channel,
      languageCode,
      skus: skus.join(','),
      limit: STRAP_SIZES_FETCH_LIMIT,
    },
  });

  try {
    const response = await fetch(url);
    const data = await response.json();

    if (response.ok && data?.success) {
      return data;
    } else {
      return new Error(data?.message);
    }
  } catch (error) {
    return [];
  }
};

export const fetchProductsBySkus = async ({
  languageCode,
  skus,
}: {
  languageCode: LanguageCodeEnum;
  skus: string[];
}): Promise<ProductsBySkusQuery | undefined> => {
  if (!skus.length) return;

  const url = paths.api.products.asPath({
    query: {
      languageCode,
      skus: skus.join(','),
    },
  });

  const response = await fetch(url);
  const data = await response.json();

  if (response.ok && data?.success) {
    return data.data as ProductsBySkusQuery;
  }
};

export const fetchProductDetailPrivate = async ({
  channel,
  languageCode,
  sku,
}: {
  channel: string;
  languageCode: LanguageCodeEnum;
  sku: string;
}) => {
  if (!sku || !languageCode || !channel) return;

  const response = await fetch(paths.api.productDetail.asPath(), {
    method: 'POST',
    body: JSON.stringify({
      sku,
      channel,
      languageCode,
      thumbnailSize: DETAIL_PAGE_IMAGE_SIZE,
      imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
      largeMediaSize: DETAIL_PAGE_LARGE_IMAGE_SIZE,
    }),
  });

  if (response.ok) {
    const data = await response.json();
    return data as ProductVariantWatchBasicDetailsQuery;
  }
};

export const fetchProductDetail = createDebouncedFunction(
  fetchProductDetailPrivate,
  800,
);
